import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";
import { CellContext } from "@tanstack/react-table";

import { GroupOverviewData } from "../../GroupPage.utils";

const useStyles = makeStyles()((theme) => ({
    box: { display: "flex", alignItems: "center" },
    cellContent: {
        display: "inline-block",
        maxWidth: 240,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        position: "relative",
    },
    defaultBadge: {
        backgroundColor: theme.palette.emteria.primary,
        color: theme.palette.emteria.text.inverse,
        padding: theme.spacing(1, 2),
        borderRadius: 12,
        minWidth: 52,
        marginLeft: theme.spacing(2),
        lineHeight: 1,
    },
    highlightedText: {
        backgroundColor: theme.palette.emteria.warning,
    },
}));

type Props = CellContext<GroupOverviewData, unknown> & { withIndent?: boolean };

const TableCell = (props: React.PropsWithChildren<Props>) => {
    const [isTruncated, setIsTruncated] = React.useState(false);
    const textRef = React.useRef<HTMLSpanElement>(null);

    const { t } = useTranslation(["pages"]);
    const { classes } = useStyles();

    const value = props.getValue<string>();
    const globalFilter: string = props.table.getState().globalFilter;
    const shouldHighlight = globalFilter && value.toLowerCase().includes(globalFilter.toLowerCase());
    const regex = new RegExp(`(${globalFilter})`, "gi");
    const parts = value?.split(regex);

    React.useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        }
    }, [value]);

    return (
        <Tooltip title={isTruncated ? value : ""} placement="top" PopperProps={{ disablePortal: false }}>
            <Box className={classes.box} style={{ paddingLeft: props.withIndent ? `${props.row.depth * 2}rem` : "unset" }}>
                {props.children}
                <Typography variant="body1" component="span" className={classes.cellContent} ref={textRef}>
                    {shouldHighlight
                        ? parts.map((part, index) =>
                              part.toLowerCase() === globalFilter.toLowerCase() ? (
                                  <span key={index} className={classes.highlightedText}>
                                      {part}
                                  </span>
                              ) : (
                                  part
                              ),
                          )
                        : value}
                </Typography>
                {props.column.id === "name" && props.row.original.isDefault && (
                    <Box className={classes.defaultBadge}>
                        <Typography variant="caption" component="span" sx={{ lineHeight: 1 }}>
                            {t("pages:groupPage.table.default")}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
};

export default TableCell;
